/**
 * Minimap framing (plan 13 §3). One uniform scale for both axes, so platforms
 * keep their shape. The scale shows about MINIMAP_VIEW_WIDTH world px across
 * (the guidance cards' widest window), never less than the whole map's fit and
 * never more zoomed than filling the box. Per axis: a map that fits is
 * centred (letterboxed); a longer one is windowed around the player.
 *
 * Zoom (DJ, plan 13 §9.3): the player steps the window width through
 * MINIMAP_ZOOM_STEPS with the minimap's +/− buttons, from a close-up to the
 * whole level; the default step is MINIMAP_VIEW_WIDTH. The fit/fill clamps
 * hold at every step, so a small map stops changing once it shows whole.
 */

export interface MinimapView {
  /** Screen pixels per world pixel, both axes. */
  scale: number;
  /** World point drawn at the canvas origin (before `offsetX/Y`). */
  originX: number;
  originY: number;
  /** Letterbox offset in screen pixels on an axis the map fits. */
  offsetX: number;
  offsetY: number;
  /** Whether the whole map is on screen. */
  whole: boolean;
}

/** World pixels across the minimap on a wide map (matches the guidance cards). */
export const MINIMAP_VIEW_WIDTH = 4500;

/**
 * Zoom steps, closest first: world px across the minimap on a wide map.
 * `Infinity` is the whole level, letterboxed.
 */
export const MINIMAP_ZOOM_STEPS: readonly number[] = [2000, 3000, MINIMAP_VIEW_WIDTH, 7000, Infinity];
export const DEFAULT_MINIMAP_ZOOM = MINIMAP_ZOOM_STEPS.indexOf(MINIMAP_VIEW_WIDTH);
const MINIMAP_ZOOM_KEY = 'h2k.minimap.zoom';

/** The zoom step `delta` steps from `index` (+1 = out, −1 = in), clamped to the ends. */
export function stepMinimapZoom(index: number, delta: number): number {
  return Math.max(0, Math.min(MINIMAP_ZOOM_STEPS.length - 1, index + delta));
}

/** Short label for a zoom step: "2k", "4.5k", "Map". */
export function minimapZoomLabel(index: number): string {
  const across = MINIMAP_ZOOM_STEPS[index];
  if (across === undefined || !Number.isFinite(across)) return 'Map';
  return `${Number((across / 1000).toFixed(1))}k`;
}

type ZoomStorage = Pick<Storage, 'getItem' | 'setItem'>;

function browserStorage(): ZoomStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** The player's saved zoom step, or the default (no storage, blocked storage, junk value). */
export function loadMinimapZoom(storage: ZoomStorage | null = browserStorage()): number {
  try {
    const raw = storage?.getItem(MINIMAP_ZOOM_KEY);
    const index = raw === null || raw === undefined ? NaN : Number(raw);
    return Number.isInteger(index) && index >= 0 && index < MINIMAP_ZOOM_STEPS.length ? index : DEFAULT_MINIMAP_ZOOM;
  } catch {
    return DEFAULT_MINIMAP_ZOOM;
  }
}

/** Remember the zoom step (a UI preference: losing it only resets to the default). */
export function saveMinimapZoom(index: number, storage: ZoomStorage | null = browserStorage()): void {
  try {
    storage?.setItem(MINIMAP_ZOOM_KEY, String(index));
  } catch { /* private window or blocked storage: keep the in-memory choice */ }
}

function clamp(v: number, lo: number, hi: number): number {
  return hi < lo ? lo : Math.max(lo, Math.min(hi, v));
}

/** Origin and letterbox offset along one axis. */
function axis(box: number, mapSize: number, scale: number, player: number): { origin: number; offset: number; fits: boolean } {
  const extent = mapSize * scale;
  if (extent <= box + 1e-9) return { origin: 0, offset: (box - extent) / 2, fits: true };
  const view = box / scale;
  return { origin: clamp(player - view / 2, 0, mapSize - view), offset: 0, fits: false };
}

export function computeMinimapView(input: {
  width: number; height: number; mapWidth: number; mapHeight: number; playerX: number; playerY: number;
  /** World px across on a wide map (a MINIMAP_ZOOM_STEPS value); Infinity = whole map. */
  viewWidth?: number;
}): MinimapView {
  const { width, height, playerX, playerY, viewWidth = MINIMAP_VIEW_WIDTH } = input;
  const mapWidth = Math.max(1, input.mapWidth);
  const mapHeight = Math.max(1, input.mapHeight);
  const fit = Math.min(width / mapWidth, height / mapHeight);
  const fill = Math.max(width / mapWidth, height / mapHeight);
  const scale = Math.max(fit, Math.min(fill, width / Math.max(1, viewWidth)));
  const x = axis(width, mapWidth, scale, playerX);
  const y = axis(height, mapHeight, scale, playerY);
  return { scale, originX: x.origin, originY: y.origin, offsetX: x.offset, offsetY: y.offset, whole: x.fits && y.fits };
}

/** World → canvas pixels. */
export function minimapPoint(view: MinimapView, x: number, y: number): { x: number; y: number } {
  return {
    x: (x - view.originX) * view.scale + view.offsetX,
    y: (y - view.originY) * view.scale + view.offsetY,
  };
}
